import { toError } from "@clipboard-health/util-ts";
import { getFirebaseSingleton } from "@lib/firebase";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import {
  type AppListenerHandle,
  addAppStateChangeListener,
} from "@src/appV2/NativeAdapters/app.adapter";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export const useAppStateListener = () => {
  const queryClient = useQueryClient();
  const { setIsAppActive } = useAppStateContext();

  useEffect(() => {
    let listenerHandle: AppListenerHandle | undefined;

    async function setupListener() {
      try {
        listenerHandle = await addAppStateChangeListener(async ({ isActive }) => {
          logEvent(APP_V2_APP_EVENTS.APP_STATE_CHANGE, {
            isActive,
          });
          setIsAppActive(isActive);
          if (!isActive) {
            return;
          }

          const firebaseUser = getFirebaseSingleton().currentUser;
          if (firebaseUser) {
            await firebaseUser.getIdToken();
          }

          // Force refresh Cognito token if available
          // This works around an Amplify v6 bug where tokens aren't auto-refreshed
          // for CUSTOM_WITHOUT_SRP auth flow.
          // See: https://github.com/aws-amplify/amplify-js/issues/14618
          try {
            const cognitoUser = await getCognitoSingleton().getCurrentUserInfo();
            // Force refresh token if user exists
            if (cognitoUser) {
              await getCognitoSingleton().getIdToken(true);
            }
          } catch (maybeError: unknown) {
            // Cognito token refresh failure is not critical, continue with app restoration
            logError(APP_V2_APP_EVENTS.FAILED_TO_REFRESH_COGNITO_TOKEN, {
              error: toError(maybeError),
            });
          }

          logEvent(APP_V2_APP_EVENTS.APP_STATE_CHANGE_INVALIDATE_GET_WORKER_QUERY);
          await invalidateGetWorkerQuery(queryClient);

          logEvent(APP_V2_APP_EVENTS.APP_RESTORED, {
            isAuthorizedInFirebase: Boolean(firebaseUser),
          });
        });
      } catch (maybeError: unknown) {
        const error = toError(maybeError);

        logError(APP_V2_APP_EVENTS.FAILED_TO_SET_UP_APP_STATE_LISTENER, {
          error,
        });
      }
    }

    void setupListener();

    return () => {
      void listenerHandle?.remove();
    };
  }, [queryClient, setIsAppActive]);
};
